
import { db, functions, auth, googleProvider } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { 
    doc, 
    getDoc, 
    setDoc, 
    updateDoc, 
    serverTimestamp, 
    query, 
    collection, 
    where, 
    getDocs 
} from 'firebase/firestore';
import { 
    signInWithPopup, 
    signInWithCredential, 
    GoogleAuthProvider,
    User as FirebaseUser
} from 'firebase/auth';
import { UserProfile, Invitation, TeamMembership } from '../types';
import { desktop, mobile } from '../platform';

export const AuthService = {
    /**
     * Web Sign-in Flow
     */
    async signInWithGoogle() {
        const result = await signInWithPopup(auth, googleProvider);
        console.log(`[Auth] Web popup success for ${result.user.email}`);
        return result.user;
    },

    /**
     * Native (Capacitor) Sign-in Flow
     */
    async nativeSignIn() {
        console.log(`[Auth] Triggering Native (Capacitor) Google Sign-in`);
        const result = await mobile.signInWithGoogle();

        if (result?.credential?.idToken) {
            console.log(`[Auth] Native token received, bridging to Web SDK...`);
            const credential = GoogleAuthProvider.credential(result.credential.idToken);
            const userCredential = await signInWithCredential(auth, credential);
            console.log(`[Auth] Bridge success for ${userCredential.user.email}`);
            return userCredential.user;
        }
        throw new Error("No idToken received from native sign-in");
    },

    /**
     * Tauri (Desktop) Sign-in Flow
     */
    async tauriSignIn(onSuccess: (user: FirebaseUser) => Promise<void>) {
        console.log(`[Auth] Triggering Tauri (Desktop) OAuth Flow`);
        const port = 1420;
        const redirectUri = `http://localhost:${port}`;
        const clientId = '207380369862-qjns4kqs1tl5fgqefpa4ilicp1866ern.apps.googleusercontent.com';

        const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
            `client_id=${clientId}&` +
            `redirect_uri=${encodeURIComponent(redirectUri)}&` +
            `response_type=id_token&` +
            `scope=openid%20profile%20email&` +
            `nonce=${Math.random().toString(36).substring(2)}`;

        // 1. Wake up the Rust OAuth server
        await desktop.invoke('start_oauth_server');

        // 2. Listen for the token
        const unlisten = await desktop.listen<string>('oauth://url', async (payload: any) => {
            try {
                const url = new URL(payload);
                const hash = url.hash.substring(1);
                const params = new URLSearchParams(hash);
                const idToken = params.get('id_token');

                if (idToken) {
                    console.log(`[Auth] Tauri OAuth token received`);
                    const credential = GoogleAuthProvider.credential(idToken);
                    await signInWithCredential(auth, credential);
                    console.log(`[Auth] Desktop sign-in success`);
                    if (auth.currentUser) {
                        await onSuccess(auth.currentUser);
                    }
                    unlisten();
                }
            } catch (e) {
                console.error("[Auth Error] Tauri OAuth event failed:", e);
            }
        });

        // 3. Open the browser
        await desktop.openUrl(authUrl);
    },

    /**
     * FULL RESTORE: New User Onboarding & Orphan Recovery logic
     */
    async handleUserSync(firebaseUser: FirebaseUser) {
        console.log(`[Sync] Synchronizing profile for ${firebaseUser.email}`);

        const userRef = doc(db, 'users', firebaseUser.uid);
        const userSnap = await getDoc(userRef);
        const email = firebaseUser.email?.toLowerCase();

        if (userSnap.exists()) {
            const dbProfile = userSnap.data() as UserProfile;
            console.log(`[Sync] Existing user profile found in DB. Org: ${dbProfile.orgId}`);
            const updates: any = {
                displayName: firebaseUser.displayName,
                photoURL: firebaseUser.photoURL || (firebaseUser as any).photoUrl || '',
                lastLogin: serverTimestamp()
            };

            // Orphan user recovery logic
            if (!dbProfile.orgId && email) {
                console.log(`[Sync] Orphan user detected, attempting domain recovery...`);
                const domain = email.split('@')[1];

                const ownerSnap = await getDocs(query(collection(db, 'organizations'), where('ownerEmail', '==', email)));
                if (!ownerSnap.empty) {
                    console.log(`[Sync] Matched as Organization Owner`);
                    updates.orgId = ownerSnap.docs[0].id;
                    updates.isOrgAdmin = true;
                    await updateDoc(ownerSnap.docs[0].ref, { ownerId: firebaseUser.uid });
                } else if (domain) {
                    const domainSnap = await getDocs(query(collection(db, 'organizations'), where('domain', '==', domain)));
                    if (!domainSnap.empty) {
                        console.log(`[Sync] Matched by Email Domain: ${domain}`);
                        updates.orgId = domainSnap.docs[0].id;
                    }
                }
            }

            // AUTO-HEALING: Ensure orgIds array exists
            if (updates.orgId && (!dbProfile.orgIds || dbProfile.orgIds.length === 0)) {
                updates.orgIds = [updates.orgId];
            } else if (dbProfile.orgId && (!dbProfile.orgIds || dbProfile.orgIds.length === 0)) {
                updates.orgIds = [dbProfile.orgId];
            }

            await updateDoc(userRef, updates);
            console.log(`[Sync] Profile update complete`);
            return;
        }

        // --- NEW SAAS ONBOARDING LOGIC ---
        console.log(`[Sync] Truly new user detected, initiating onboarding...`);
        let finalOrgId = '';
        let isOrgAdmin = false;

        if (email) {
            try {
                // 1. Check for pending invitations
                const inviteSnap = await getDocs(query(collection(db, 'invitations'),
                    where('inviteeId', '==', firebaseUser.uid),
                    where('status', '==', 'pending')
                ));

                if (!inviteSnap.empty) {
                    const invite = inviteSnap.docs[0].data() as Invitation;
                    finalOrgId = invite.orgId;
                    console.log(`[Sync] User has pending invite, assigning to Org: ${finalOrgId}`);
                } else {
                    // 2. Check if Org Owner
                    const ownerQuery = query(collection(db, 'organizations'), where('ownerEmail', '==', email));
                    const ownerSnap = await getDocs(ownerQuery);

                    if (!ownerSnap.empty) {
                        const orgDoc = ownerSnap.docs[0];
                        finalOrgId = orgDoc.id;
                        isOrgAdmin = true;
                        console.log(`[Sync] Initializing as Org Owner for ${finalOrgId}`);
                        await updateDoc(orgDoc.ref, { ownerId: firebaseUser.uid });
                    } else {
                        // 3. Check Domain match
                        const domain = email.split('@')[1];
                        if (domain) {
                            const domainQuery = query(collection(db, 'organizations'), where('domain', '==', domain), where('type', '==', 'enterprise'));
                            const domainSnap = await getDocs(domainQuery);
                            if (!domainSnap.empty) {
                                finalOrgId = domainSnap.docs[0].id;
                                console.log(`[Sync] Matched by Enterprise Email Domain: ${domain}, Org: ${finalOrgId}`);
                            }
                        }
                    }
                }
            } catch (e) {
                console.error("SaaS Onboarding lookup failed", e);
            }
        }

        // 4. Auto-provision personal workspace
        if (!finalOrgId) {
            console.log(`[Sync] No existing org found, auto-provisioning personal workspace...`);
            const createOrgFunc = httpsCallable(functions, 'createPersonalWorkspace');
            try {
                const result = await createOrgFunc({ displayName: firebaseUser.displayName, email: email });
                finalOrgId = (result.data as { orgId: string }).orgId;
                console.log(`[Sync] Auto-provisioned new personal Org: ${finalOrgId}`);
            } catch (e) {
                console.error("[Sync Error] Failed to auto-provision personal workspace:", e);
                throw e;
            }
        }

        const newProfile: Partial<UserProfile> = {
            email: email || '',
            displayName: firebaseUser.displayName || 'New User',
            photoURL: firebaseUser.photoURL || (firebaseUser as any).photoUrl || '',
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            orgId: finalOrgId,
            orgIds: finalOrgId ? [finalOrgId] : [],
            isOrgAdmin: isOrgAdmin,
            createdAt: Date.now() as any
        };

        await setDoc(userRef, newProfile);
    },

    /**
     * Triggers the cloud function to create a personal workspace for a new user.
     */
    async logout() {
        await auth.signOut();
    },

    async createPersonalWorkspace(displayName: string | null, email: string | null) {
        const createOrgFunc = httpsCallable(functions, 'createPersonalWorkspace');
        return await createOrgFunc({ displayName, email });
    },

    /**
     * Triggers the cloud function to accept an email invitation using a token.
     */
    async acceptEmailInvite(token: string) {
        const acceptFunc = httpsCallable(functions, 'acceptEmailInvite');
        return await acceptFunc({ token });
    }
};
